import React from 'react';
import { AccountData } from '../types';
import Button from './Button';
import UserIcon from './icons/UserIcon';
import EnvelopeIcon from './icons/EnvelopeIcon';
import CalendarDaysIcon from './icons/CalendarDaysIcon';
import CreditCardIcon from './icons/CreditCardIcon';
import QuestionMarkCircleIcon from './icons/QuestionMarkCircleIcon';
import { useTranslation } from 'react-i18next';

interface AccountViewProps {
  accountData: AccountData | null;
  onShowTransactionHistory: () => void;
  onGoToCheckout: () => void;
  onOpenHelperMenu: () => void;
}

const InfoRow: React.FC<{ icon: React.ReactNode; label: string; value: string }> = ({ icon, label, value }) => (
  <div className="flex items-center justify-between p-3 sm:p-4 gap-4">
    <div className="flex items-center text-slate-600 flex-shrink-0">
      {icon}
      <span className="font-medium text-sm sm:text-base ml-3">{label}</span>
    </div>
    <span className="text-slate-800 font-semibold text-sm sm:text-base text-right break-all min-w-0">{value}</span>
  </div>
);

const AccountView: React.FC<AccountViewProps> = ({ accountData, onShowTransactionHistory, onGoToCheckout, onOpenHelperMenu }) => {
  const { t } = useTranslation();
  if (!accountData) {
    return <div>{t('accountView.loading')}</div>;
  }

  const handleContactClick = () => {
    window.location.href = 'mailto:podrska@klikero.com';
  };

  return (
    <div className="bg-white p-4 sm:p-6 md:p-8 shadow-xl rounded-lg w-full max-w-2xl mx-auto">
      <h2 className="text-2xl md:text-3xl font-bold text-center text-sky-700 mb-8">{t('accountView.title')}</h2>

      <div className="bg-sky-50 border-2 border-sky-200 rounded-xl p-4 md:p-6 text-center mb-8">
        <p className="text-sm font-medium text-sky-800">{t('accountView.creditsRemaining')}</p>
        <p className="text-4xl md:text-5xl font-bold text-sky-700 my-2">{accountData.credits}</p>
        {accountData.credits < 10 && (
          <p className="text-red-600 text-sm mt-2">{t('accountView.lowCreditsWarning')}</p>
        )}
      </div>

      <div className="bg-slate-50 rounded-xl border border-slate-200/80 divide-y divide-slate-200">
        <InfoRow icon={<UserIcon className="w-5 h-5" />} label={t('accountView.usernameLabel')} value={accountData.username} />
        <InfoRow icon={<EnvelopeIcon className="w-5 h-5" />} label={t('accountView.emailLabel')} value={accountData.email} />
        <InfoRow icon={<CalendarDaysIcon className="w-5 h-5" />} label={t('accountView.memberSinceLabel')} value={accountData.memberSince} />
      </div>

      <div className="mt-8 space-y-4">
        <Button size="lg" onClick={onGoToCheckout} className="w-full">
          <CreditCardIcon className="w-5 h-5 mr-2" />
          {t('accountView.topUpButton')}
        </Button>
        <div className="text-center">
          <Button onClick={onShowTransactionHistory} variant="link">{t('accountView.transactionHistoryLink')}</Button>
        </div>
      </div>

      {/* Help Section for Mobile */}
      <div className="md:hidden mt-8 pt-6 border-t border-slate-200">
        <h3 className="text-lg font-semibold text-slate-800 text-center mb-4">{t('accountView.helpTitle')}</h3>
        <div className="space-y-3">
          <Button
            onClick={onOpenHelperMenu}
            variant="secondary"
            className="w-full flex items-center justify-center space-x-3"
          >
            <QuestionMarkCircleIcon className="w-5 h-5" />
            <span>{t('accountView.faqButton')}</span>
          </Button>
          <Button
            onClick={handleContactClick}
            variant="secondary"
            className="w-full flex items-center justify-center space-x-3"
          >
            <EnvelopeIcon className="w-5 h-5" />
            <span>{t('accountView.contactButton')}</span>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AccountView;